import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { searchIndex } from "@/data/site";

// Türkçe karakterleri aramada eşleşme hataları yaratmaması için
// hem sorguyu hem de indeks metnini ASCII'ye indirger.
function normalize(s: string): string {
  return s
    .toLocaleLowerCase("tr")
    .replace(/ç/g, "c")
    .replace(/ğ/g, "g")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ş/g, "s")
    .replace(/ü/g, "u");
}

export default function HeaderSearch({ autoFocus = false }: { autoFocus?: boolean }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const hasQuery = query.trim().length > 0;

  const results = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    const qn = normalize(q);
    const words = qn.split(/\s+/).filter(Boolean);

    const scored = searchIndex
      .map((item) => {
        const labelN = normalize(item.label);
        const hay = normalize(`${item.label} ${item.keywords} ${item.group ?? ""}`);
        let score = 0;

        if (labelN === qn) score += 100;
        else if (labelN.startsWith(qn)) score += 60;
        else if (labelN.includes(qn)) score += 40;
        else if (hay.includes(qn)) score += 20;

        for (const w of words) {
          if (labelN.includes(w)) score += 15;
          else if (hay.includes(w)) score += 6;
        }

        if (words.length > 1 && words.every((w) => hay.includes(w))) score += 12;

        return { item, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);

    return scored.slice(0, 10).map((x) => x.item);
  }, [query]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const close = () => {
    setOpen(false);
    setActiveIndex(-1);
  };

  const select = (href: string) => {
    setQuery("");
    close();
    navigate(href);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      close();
      inputRef.current?.blur();
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (results.length === 0) return;
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) =>
        e.key === "ArrowDown"
          ? (i + 1) % results.length
          : i <= 0
            ? results.length - 1
            : i - 1
      );
      return;
    }
    if (e.key === "Enter") {
      if (open && activeIndex >= 0 && results[activeIndex]) {
        e.preventDefault();
        select(results[activeIndex].href);
      } else if (results.length > 0) {
        e.preventDefault();
        select(results[0].href);
      }
    }
  };

  return (
    <div ref={rootRef} className="relative w-full">
      <div className="relative">
        <i
          className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base pointer-events-none"
          aria-hidden="true"
        ></i>
        <input
          ref={inputRef}
          type="text"
          autoFocus={autoFocus}
          role="combobox"
          aria-expanded={open && hasQuery}
          aria-controls="site-search-listbox"
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 ? `site-search-option-${activeIndex}` : undefined
          }
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Ders, sınav, konu veya sayfa ara…"
          className="w-full h-11 pl-10 pr-10 rounded-md border border-background-200 bg-background-100/80 text-sm text-foreground-900 placeholder:text-foreground-400 hover:border-background-300 focus:border-primary-400 focus:bg-background-50 focus:outline-none transition-colors"
        />
        {hasQuery && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setActiveIndex(-1);
              inputRef.current?.focus();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded text-foreground-400 hover:text-foreground-700 transition-colors"
            aria-label="Aramayı temizle"
          >
            <i className="ri-close-line text-base" aria-hidden="true"></i>
          </button>
        )}
      </div>

      {open && hasQuery && (
        <div className="absolute top-full left-0 right-0 mt-2 z-50">
          <ul
            id="site-search-listbox"
            role="listbox"
            aria-label="Arama sonuçları"
            className="rounded-lg border border-background-200 bg-background-50 p-1.5 max-h-96 overflow-y-auto"
          >
            {results.length === 0 ? (
              <li className="px-3 py-4 text-sm text-foreground-500 text-center">
                <i className="ri-search-eye-line block text-lg mb-1" aria-hidden="true"></i>
                Eşleşen sayfa bulunamadı
              </li>
            ) : (
              results.map((item, i) => (
                <li
                  key={`${item.href}-${item.label}`}
                  role="option"
                  id={`site-search-option-${i}`}
                  aria-selected={i === activeIndex}
                >
                  <button
                    type="button"
                    onClick={() => select(item.href)}
                    onMouseEnter={() => setActiveIndex(i)}
                    className={`w-full flex items-center gap-2 rounded-md px-3 py-2.5 text-left text-sm transition-colors ${
                      i === activeIndex
                        ? "bg-primary-50 text-primary-700"
                        : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    <span className="truncate flex-1">{item.label}</span>
                    {item.group && (
                      <span className="shrink-0 text-[11px] font-medium text-foreground-400 whitespace-nowrap">
                        {item.group}
                      </span>
                    )}
                    <i
                      className="ri-arrow-right-line text-foreground-400 shrink-0"
                      aria-hidden="true"
                    ></i>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}